/* eslint-disable max-lines */
/* eslint-disable react-func/max-lines-per-function */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { message as EeveeMessage, Modal } from '@fuxi/eevee-ui';
import { CheckItem, CreateTaskPayload, TaskJson, EditingTaskJson, ParentTaskMode } from 'common/types';
import { navToMySales } from 'common/router/config';
import { FastApiErrorStatusCode, FastApiStatusCode } from 'common/service/constants/fastApi';
import service from '@/service';
import { TabItemSource } from '@/types';
import { TaskTemplateCreateParams, UpdateParentTaskParams } from '@/service/taskInstance';
import {
  filterExistingStages,
  getStagesTemplateSortList,
  updateStagesInStash,
  updateStagesTemplateSortList,
  validateTaskJson,
} from '@/utils/stagesInStashUpdater';

import { RootState } from '../rootReducer';
import { AppThunk } from '..';

// json配置状态: 模版配置 发布任务 编辑任务

export const getTabLabel = ({
  item,
  activeStage,
  FILE_TYPES_OPTIONS,
}: {
  item?: TabItemSource;
  activeStage?: string;
  FILE_TYPES_OPTIONS?: any[];
}) => {
  if (!item) {
    return '';
  }
  const { checkItemType, fileTypes } = item;
  const checkItemTypeLabel = FILE_TYPES_OPTIONS?.find?.(({ type }) => type === checkItemType)?.label || checkItemType;

  if (!activeStage) {
    return `${checkItemTypeLabel}${fileTypes?.reduce((acc, cur) => {
      return acc + '/' + cur;
    }, '')}`;
  }

  return `${activeStage}/${checkItemTypeLabel}${fileTypes?.reduce((acc, cur) => {
    return acc + '/' + cur;
  }, '')}`;
};

export enum ResponseStatus {
  SUCCESS = 'success',
  ERROR = 'error',
  LOADING = 'loading',
  OVERDRAFT = 'overdraft',
}

type State = {
  parentTemplateId?: number;
  templateName: string;
  stageTemplateSortList: number[]; // 子阶段的id顺序
  responseStatus?: ResponseStatus;
  isCheckingForms: boolean; // true 触发form表单校验，标红
  // 配置状态UI
  launcherOptions: Array<any>; // 任务下可选的launcher
  // 配置中的子任务的stage-左侧菜单面板
  taskChildren: EditingTaskJson[];
  activeChildTask: {
    id?: number;
    stage?: string;
    checkList?: CheckItem[];
    tabsSource: TabItemSource[]; // 子任务的checklist的重组数据展示-右侧标签页集合
  };
  // 配置中的子任务的rule主体-右侧tab标题
  activeTabKey: string;
  parentTaskId?: number; // 定向发布成功，返回的任务id
  isEditingTask: boolean;
  excludeExternals?: number[]; //排查制作方账号
  taskMode?: ParentTaskMode; // 当前编辑任务的模式，用于 gating 追加阶段入口（仅委托模式支持）
  projectId?: string | number; // 当前编辑任务所属项目，用于追加阶段时按项目拉取工作流/launcher
  /**
   * 本次会话通过「+ 新增文件类型」加进来的 tab（itemId 集合）。
   * 这些 tab 尚未落库、必填项还是空的，需按「模版」语义渲染以便填写，详见 isTabEditable。
   */
  newTabItemIds?: string[];
};

const INIT_VALUE = {
  templateName: '',
  stageTemplateSortList: [],
  isCheckingForms: false,
  launcherOptions: [],
  taskChildren: [],
  activeChildTask: {
    stage: '',
    checkList: [],
    tabsSource: [],
  },
  activeTabKey: '',
  isEditingTask: false,
  taskMode: ParentTaskMode.Unknown,
  newTabItemIds: [],
};

const initialState: State = INIT_VALUE;

const taskSettings = createSlice({
  name: 'taskSettings',
  initialState,
  reducers: {
    initialValue(state) {
      return INIT_VALUE;
    },
    updateValue(state, { payload }: PayloadAction<Partial<State>>) {
      const newState = Object.assign({}, { ...state, ...payload });
      return newState;
    },
    setIsCheckingForms(state, { payload }: PayloadAction<boolean>) {
      state.isCheckingForms = payload;
    },
    setLauncherOptions(state, { payload }: PayloadAction<Array<any>>) {
      state.launcherOptions = payload;
    },
    updateChildTask(state, { payload }: PayloadAction<{ stageId: number; newStageSetting: Partial<EditingTaskJson> }>) {
      const { stageId, newStageSetting } = payload;
      const newTaskChildren = updateStagesInStash(state.taskChildren, stageId, {
        ...newStageSetting,
        status: 'edited',
      });
      state.taskChildren = newTaskChildren;
      const isActive = state.activeChildTask?.id === stageId;
      if (isActive && newStageSetting.stage !== undefined) {
        // 仅当本次显式更新了 stage 名时才同步，避免 externalContacts-only 等改动把
        // activeChildTask.stage 覆写为 undefined（否则 StagesNav 会误判为"无选中阶段"并重置到首项）
        state.activeChildTask = Object.assign({}, state.activeChildTask, { stage: newStageSetting.stage });
      }
    },
    setIsEditingTask(state, { payload }: PayloadAction<boolean>) {
      state.isEditingTask = payload;
    },
  },
});

export const { initialValue, updateValue, setIsCheckingForms, setLauncherOptions, updateChildTask, setIsEditingTask } =
  taskSettings.actions;

export const selectTaskSettings = (state: RootState) => state.taskSettings;
export const selectIsEditingTask = (state: RootState) => state.taskSettings.isEditingTask;

export const initSettingOptions =
  (projectId): AppThunk =>
  async (dispatch, getState) => {
    const launcherOptions = (await service.dynamicCheckItemSetting.getLauncherOptions(projectId)) || [];
    dispatch(setLauncherOptions(launcherOptions));
  };

/**
 * 父级制作方变化时，把各阶段 externalContacts 中不在父级的 id 剔除。
 * 剪枝必须同时把对应阶段标为 'edited'（新阶段经 changeStatus 仍保持 'new'），
 * 否则提交时该阶段不会进入 stageExternalContacts/newStages，后端仍以旧值校验
 * 子集会阻断整次修改（含父级本身）。详见 doc §七 校验规则。
 * 仅当确实有阶段被裁剪时才更新，避免循环。
 */
export const syncStageExternalContactsToParent =
  (parentIds: number[]): AppThunk =>
  (dispatch, getState) => {
    const { taskChildren } = getState().taskSettings;
    if (!taskChildren?.length) return;
    const parentSet = new Set(parentIds?.map(Number));
    let changed = false;
    let nextTaskChildren = taskChildren;
    taskChildren.forEach((task) => {
      const ids = task.externalContacts;
      if (Array.isArray(ids) && ids.length) {
        const filtered = ids.filter((id) => parentSet.has(Number(id)));
        if (filtered.length !== ids.length) {
          changed = true;
          // 经 updateStagesInStash 走 changeStatus：'new' 阶段仍为 'new'（→ newStages 透传），
          // 已存在阶段变 'edited'（→ stageExternalContacts 提交），确保裁剪后的值真正落库
          nextTaskChildren = updateStagesInStash(nextTaskChildren, task.id, {
            externalContacts: filtered,
            status: 'edited',
          });
        }
      }
    });
    if (changed) {
      dispatch(updateValue({ taskChildren: nextTaskChildren }));
    }
  };

// 接口请求
export const addTemplateRequest = (): AppThunk => async (dispatch, getState) => {
  const store = getState();
  const projectId = store.settings.activeProject?.projectId;
  if (!projectId) {
    EeveeMessage.error('异常，刷新后重试');
    return;
  }
  const { templateName, taskChildren } = store.taskSettings;
  if (!templateName) {
    EeveeMessage.error('模版名称不能为空');
    return;
  }
  const pureTaskChildren: TaskJson[] = filterExistingStages(taskChildren);
  const isReady = validateTaskJson(pureTaskChildren);
  if (!isReady) {
    return;
  }
  // 全量更新
  dispatch(updateValue({ responseStatus: ResponseStatus.LOADING }));
  try {
    pureTaskChildren?.map((task) => {
      const { template, stage, launcherId, workFlow } = task;
      return { template, stage, launcherId, workFlow };
    });
    const isSuc = await service.taskTempSetting.addParentTemplate(projectId!, templateName, pureTaskChildren);
    dispatch(updateValue({ responseStatus: isSuc ? ResponseStatus.SUCCESS : ResponseStatus.ERROR }));
  } catch (e) {
    dispatch(updateValue({ responseStatus: ResponseStatus.ERROR }));
  }
  dispatch(updateValue({ responseStatus: ResponseStatus.SUCCESS }));
};

export const updateTemplateRequest = (): AppThunk => async (dispatch, getState) => {
  const store = getState();
  const { parentTemplateId, templateName, taskChildren } = store.taskSettings;
  const projectId = store.settings.activeProject?.projectId;
  if (!projectId || !parentTemplateId) {
    EeveeMessage.error('异常，刷新后重试');
    return;
  }
  const pureTaskChildren: TaskJson[] = filterExistingStages(taskChildren);
  const isReady = validateTaskJson(pureTaskChildren);
  if (!isReady) {
    return;
  }

  // 检查规则配置的用户输入？
  // 前端缓存用户修改操作，点击确认后发起，依次修改子阶段更新后再修改总阶段
  dispatch(updateValue({ responseStatus: ResponseStatus.LOADING }));
  try {
    const updates = taskChildren.filter(({ status }) => status && ['deleted', 'new', 'edited'].includes(status)) || [];
    let stageTemplateSortList = getStagesTemplateSortList(taskChildren);
    const results = await Promise.allSettled(
      updates?.map(async (task) => {
        const { id, stage, launcherId, workFlow, template } = task;
        switch (task.status) {
          case 'deleted':
            return await service.taskTempSetting.removeStageTemplate(projectId, parentTemplateId!, id!);
          case 'new':
            const newId = await service.taskTempSetting.updateStageTemplate(
              { stage, launcherId, workFlow, template },
              projectId,
              parentTemplateId,
            );
            stageTemplateSortList = updateStagesTemplateSortList(stageTemplateSortList, id, newId);
            return !!newId;
          default:
            const newId1 = await service.taskTempSetting.updateStageTemplate(
              { stage, launcherId, workFlow, template },
              projectId,
              parentTemplateId,
              id,
            );
            stageTemplateSortList = updateStagesTemplateSortList(stageTemplateSortList, id, newId1);
            return !!newId1;
        }
      }),
    );
    const isStagesChanged = (results as any).every(({ value }) => value === true);
    if (!isStagesChanged) {
      throw new Error();
    }
    await service.taskTempSetting.updateParentTemplate(
      projectId,
      parentTemplateId!,
      templateName,
      stageTemplateSortList as number[],
    );
    EeveeMessage.success('更新模版成功');
    dispatch(updateValue({ responseStatus: ResponseStatus.SUCCESS }));
  } catch (e) {
    EeveeMessage.error('修改失败，请检查重试');
    dispatch(updateValue({ responseStatus: ResponseStatus.ERROR }));
  }
};

export const publishTaskRequest =
  (basicInfo: Partial<CreateTaskPayload>): AppThunk =>
  async (dispatch, getState) => {
    const isUploading = basicInfo?.attachment?.some(({ status }) => status === 'uploading');
    if (isUploading) {
      Modal.warning({
        title: '请稍后重试',
        content: '文件还在上传中',
        okText: '我知道了',
      });
      return;
    }
    const store = getState();
    const { userInfo } = store.user;

    // 获取模板配置
    const { taskChildren } = store.taskSettings;
    const pureTaskChildren: TaskJson[] = filterExistingStages(taskChildren)?.filter?.(({ isChosen }) => isChosen);
    const isReady = validateTaskJson(pureTaskChildren);
    if (!isReady) {
      return;
    }
    // 全量更新
    dispatch(updateValue({ responseStatus: ResponseStatus.LOADING }));

    try {
      pureTaskChildren?.map((task) => {
        const { template, stage, launcherId, workFlow, id } = task;
        return { template, stage, launcherId, workFlow, id };
      });
      const isAllocatorSelf = userInfo?.uniqueId === basicInfo.allocator;

      const { code, data, message } = await service.taskInstance.createParentTaskWithTemplate({
        ...basicInfo,
        stages: pureTaskChildren,
      } as TaskTemplateCreateParams);
      const isSuc = code === 0;
      // 0.7.1.6新增code状态
      if (code === FastApiStatusCode.YXBlackUsers) {
        EeveeMessage.error('该用户为营销不合作用户,请重新选择');
        dispatch(updateValue({ responseStatus: ResponseStatus.ERROR, excludeExternals: data }));
        return;
      } else if (code === FastApiStatusCode.YSExclusiveUsers) {
        EeveeMessage.error('该制作方已下架,请重新选择');
        dispatch(updateValue({ responseStatus: ResponseStatus.ERROR, excludeExternals: data }));
        return;
      } else if (code === FastApiStatusCode.YSBlackUsers) {
        EeveeMessage.error('该用户为艺设不合作用户,请重新选择');
        dispatch(updateValue({ responseStatus: ResponseStatus.ERROR, excludeExternals: data }));
        return;
      }
      const isOverdraft = code === FastApiStatusCode.EmptyReimbursementInformation;
      if (isOverdraft) {
        dispatch(updateValue({ responseStatus: ResponseStatus.OVERDRAFT }));
        return;
      }
      const isPaymentFailed = code === FastApiStatusCode.ZhongbaoUserInsufficientBalance;
      if (isPaymentFailed && isAllocatorSelf) {
        Modal.confirm({
          title: '余额不足',
          content: '当前余额不足，请充值后发布任务',
          okText: '去充值',
          cancelText: '稍后处理',
          onOk: () => {
            window.location.href = navToMySales({ recharge: 'true' });
          },
        });
      }
      if (isPaymentFailed && !isAllocatorSelf) {
        Modal.warning({
          title: '余额不足',
          content: '需求方账户余额不足',
          okText: '我知道了',
        });
      }
      if (!isSuc) {
        EeveeMessage.error(message || '发布任务失败');
        dispatch(updateValue({ responseStatus: ResponseStatus.ERROR }));
      } else {
        dispatch(updateValue({ responseStatus: ResponseStatus.SUCCESS, parentTaskId: data?.parentTaskId }));
      }
    } catch (e) {
      dispatch(updateValue({ responseStatus: ResponseStatus.ERROR }));
    }
  };

export const updateTaskRequest =
  (parentTaskId: number, basicInfo: Partial<CreateTaskPayload>): AppThunk =>
  async (dispatch, getState) => {
    const store = getState();
    const { userInfo } = store.user;
    const { taskChildren } = store.taskSettings;
    const pureTaskChildren: TaskJson[] = filterExistingStages(taskChildren)?.filter?.(({ isChosen }) => isChosen);
    const isReady = validateTaskJson(pureTaskChildren);
    if (!isReady) {
      return;
    }
    // 检查规则配置的用户输入？
    // 前端缓存用户修改操作，点击确认后发起，依次修改子阶段更新后再修改总阶段
    dispatch(updateValue({ responseStatus: ResponseStatus.LOADING }));
    try {
      const updates = taskChildren.filter(({ status, isChosen }) => isChosen && status === 'edited') || [];
      // 本次临时追加的新阶段：委托模式下后端在 updateParentTask 内一次性落库
      const newStages = (taskChildren || [])
        .filter(({ status, isChosen }) => isChosen && status === 'new')
        .map(({ stage, launcherId, workFlow, template, externalContacts }) => ({
          stage,
          launcherId,
          workFlow,
          template,
          // 仅当显式指派为数组时才带 externalContacts（null/undefined 不发 → 回退父级全集）
          ...(Array.isArray(externalContacts) ? { externalContacts } : {}),
        })) as TaskJson[];
      // 已存在阶段制作方改动：按 stage 名批量提交到 updateParentTask 顶层字段
      // 仅提交用户实际编辑过(且显式指派为数组)的阶段，未触碰的不发 → 后端保持不变
      // externalContacts 可能为 null（编辑态回填的"未单独指派"），不作为值提交
      const stageExternalContacts = updates
        .filter(({ externalContacts }) => Array.isArray(externalContacts))
        .map(({ stage, externalContacts }) => ({ stage, externalContacts: externalContacts as number[] }));
      const {
        data,
        code,
        message: msg,
      } = await service.taskInstance.updateParentTask(parentTaskId, {
        ...basicInfo,
        newStages,
        stageExternalContacts,
      } as UpdateParentTaskParams);
      const isOverdraft = code === FastApiStatusCode.EmptyReimbursementInformation;
      if (isOverdraft) {
        dispatch(updateValue({ responseStatus: ResponseStatus.OVERDRAFT }));
        return;
      }
      let isSuc = code === 0;
      // 0.7.1.6新增code状态
      if (code === FastApiStatusCode.YXBlackUsers) {
        EeveeMessage.error('该制作者为营销不合作用户,请重新选择');
        dispatch(updateValue({ responseStatus: ResponseStatus.ERROR, excludeExternals: data }));
        return;
      } else if (code === FastApiStatusCode.YSBlackUsers) {
        EeveeMessage.error('该制作者为艺设不合作用户,请重新选择');
        dispatch(updateValue({ responseStatus: ResponseStatus.ERROR, excludeExternals: data }));
        return;
      } else if (code === FastApiStatusCode.YSExclusiveUsers) {
        EeveeMessage.error('该制作方已下架,请重新选择');
        dispatch(updateValue({ responseStatus: ResponseStatus.ERROR, excludeExternals: data }));
        return;
      }

      // 追加阶段相关错误：非委托模式 / 阶段名空或重复。直接展示后端 msg
      if (code === FastApiStatusCode.RequestOrOperationConflict || code === FastApiStatusCode.InvalidParam) {
        EeveeMessage.error(msg || '修改任务失败');
        dispatch(updateValue({ responseStatus: ResponseStatus.ERROR }));
        return;
      }

      const isPaymentFailed = code === FastApiStatusCode.ZhongbaoUserInsufficientBalance;
      const isAllocatorSelf = userInfo?.uniqueId === basicInfo.allocator;
      if (isPaymentFailed && isAllocatorSelf) {
        Modal.confirm({
          title: '余额不足',
          content: '当前余额不足，请充值后发布任务',
          okText: '去充值',
          cancelText: '稍后处理',
          onOk: () => {
            window.location.href = navToMySales({ recharge: 'true' });
          },
        });
      }
      if (isPaymentFailed && !isAllocatorSelf) {
        Modal.warning({
          title: '余额不足',
          content: '需求方账户余额不足',
          okText: '我知道了',
        });
      }
      if (!isSuc) {
        throw new Error(msg);
      }
      const results = await Promise.allSettled(
        updates?.map(async (task) => {
          const { id, stage, launcherId, workFlow, template } = task;
          return await service.taskInstance.updateTask(id!, { stage, launcherId, workFlow, template });
        }),
      );
      isSuc = (results as any).every(({ value }) => value === true);
      if (isSuc) {
        EeveeMessage.success('修改任务成功');
        dispatch(updateValue({ responseStatus: ResponseStatus.SUCCESS }));
      } else {
        throw new Error();
      }
    } catch (e) {
      EeveeMessage.error('修改失败，请检查重试');
      dispatch(updateValue({ responseStatus: ResponseStatus.ERROR }));
    }
  };

export default taskSettings.reducer;
